import React from 'react';
import { colors, gradients } from '../theme';

interface GradientButtonProps {
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  style?: React.CSSProperties;
  variant?: 'gradient' | 'outline' | 'danger' | 'ghost';
  type?: 'button' | 'submit';
  fullWidth?: boolean;
}

export default function GradientButton({
  onClick,
  disabled,
  loading,
  children,
  style,
  variant = 'gradient',
  type = 'button',
  fullWidth = false,
}: GradientButtonProps) {
  const baseStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '14px 24px',
    borderRadius: 14,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: 16,
    fontWeight: 700,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    border: 'none',
    outline: 'none',
    transition: 'opacity 0.15s, transform 0.1s',
    width: fullWidth ? '100%' : undefined,
    opacity: disabled || loading ? 0.5 : 1,
    ...style,
  };

  const variantStyle: React.CSSProperties =
    variant === 'gradient'
      ? {
          background: gradients.button,
          color: colors.textPrimary,
          boxShadow: '0 4px 24px rgba(224,64,160,0.35)',
        }
      : variant === 'outline'
      ? {
          background: 'transparent',
          color: colors.primary,
          border: `1.5px solid ${colors.primary}`,
        }
      : variant === 'danger'
      ? {
          background: 'rgba(255,69,58,0.1)',
          color: colors.error,
          border: 'none',
        }
      : {
          background: 'rgba(102,51,153,0.15)',
          color: colors.primary,
        };

  return (
    <button
      type={type}
      onClick={disabled || loading ? undefined : onClick}
      style={{ ...baseStyle, ...variantStyle }}
      onMouseEnter={(e) => {
        if (!disabled && !loading) (e.target as HTMLButtonElement).style.opacity = '0.88';
      }}
      onMouseLeave={(e) => {
        if (!disabled && !loading) (e.target as HTMLButtonElement).style.opacity = '1';
      }}
    >
      {loading ? (
        <div
          style={{
            width: 18,
            height: 18,
            border: '2.5px solid rgba(26,10,46,0.15)',
            borderTopColor: '#fff',
            borderRadius: '50%',
            animation: 'spin 0.7s linear infinite',
          }}
        />
      ) : (
        children
      )}
    </button>
  );
}
